import React from 'react';
import { IconButton, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * Reusable Minimalist Outline Remove Button (Option 2)
 *
 * Provides a clean, subtle outline in resting state and smoothly activates
 * with an error-toned border, background tint, and icon color on hover.
 */
const RemoveIconButton = ({
    onClick,
    disabled = false,
    title = 'Remove',
    label,
    size = 'small',
    edge = false,
    ariaLabel,
    stopPropagation = false,
    sx = {},
    ...props
}) => {
    const handleClick = (e) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        if (!disabled && onClick) {
            onClick(e);
        }
    };

    const button = (
        <IconButton
            size={size}
            edge={edge}
            disabled={disabled}
            onClick={handleClick}
            aria-label={ariaLabel || title}
            sx={{
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.secondary?.main || '#515F74', 0.2),
                color: 'text.secondary',
                bgcolor: 'transparent',
                borderRadius: '8px',
                p: label ? '4px 10px' : 0.75,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    borderColor: (theme) => alpha(theme.palette.error.main, 0.4),
                    bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    color: 'error.main',
                    transform: 'scale(1.04)',
                },
                '&.Mui-disabled': {
                    borderColor: (theme) => alpha(theme.palette.secondary?.main || '#515F74', 0.1),
                    color: 'action.disabled',
                    opacity: 0.4,
                    pointerEvents: 'none',
                },
                ...sx,
            }}
            {...props}
        >
            <DeleteOutlineIcon sx={{ fontSize: size === 'small' ? 18 : 20 }} />
            {label && (
                <Typography
                    component="span"
                    sx={{
                        ml: 0.75,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'inherit',
                        lineHeight: 1,
                    }}
                >
                    {label}
                </Typography>
            )}
        </IconButton>
    );

    if (!title || label) {
        return button;
    }

    return (
        <Tooltip title={title} arrow>
            <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
    );
};

export default RemoveIconButton;
